import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Search, Music, MessageCircle, ChevronRight, ChevronLeft, Briefcase, FileText, TrendingUp, Bell, Shield, Scale, AlertTriangle, Building2, Send, Sparkles } from "lucide-react";
// ─── Extracted modules ──────────────────────────────────────────────────────
import { isExecLike, isOrg, accountLabel } from './lib/accountTypes';
import { DESIGN_SYSTEM } from './constants/designSystem';
import { supabase } from './lib/supabase';
import { Toaster } from 'react-hot-toast';
import { showToast } from './utils/toast';
import { friendlyError } from './lib/utils';
import { useAudioPlayer } from './hooks/useAudioPlayer';
import { useNotifications } from './hooks/useNotifications';
import {
  isPushSupported,
  registerServiceWorker,
  requestPermission,
  subscribeAndSave,
  unsubscribeAndRemove,
} from './services/pushNotifications';

// ─── Components ─────────────────────────────────────────────────────────────
import { AppShellSkeleton, PageSkeleton, CatalogPageSkeleton } from './components/Skeleton';
import { Avatar } from './components/Avatar';
import { Badge } from './components/Badge';
import { NowPlayingBar } from './components/NowPlayingBar';
import { NotificationPanel } from './components/NotificationPanel';
import CommandPalette from './components/CommandPalette';
import { BottomTabNav } from './components/BottomTabNav';
import { FeedbackModal } from './components/FeedbackModal';
import { CookieConsent } from './components/CookieConsent';

// ─── Pages (eagerly loaded — needed immediately) ────────────────────────────
import { AuthPage } from './pages/AuthPage';
import { AccountSetupPage } from './pages/AccountSetupPage';
import { LandingPage } from './pages/LandingPage';

// ─── Pages (lazy loaded — split into separate chunks) ───────────────────────
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const DMCAPage = lazy(() => import('./pages/DMCAPage').then(m => ({ default: m.DMCAPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const CatalogPage = lazy(() => import('./pages/CatalogPage').then(m => ({ default: m.CatalogPage })));
const OpportunitiesPage = lazy(() => import('./pages/OpportunitiesPage').then(m => ({ default: m.OpportunitiesPage })));
const ResponsesPage = lazy(() => import('./pages/ResponsesPage').then(m => ({ default: m.ResponsesPage })));
const MessagesPage = lazy(() => import('./pages/MessagesPage').then(m => ({ default: m.MessagesPage })));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage').then(m => ({ default: m.PortfolioPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const ViewProfilePage = lazy(() => import('./pages/ViewProfilePage').then(m => ({ default: m.ViewProfilePage })));
const SplitGenerator = lazy(() => import('./pages/SplitGenerator/SplitGenerator'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const DealAnalyzerPage = lazy(() => import('./pages/DealAnalyzerPage').then(m => ({ default: m.DealAnalyzerPage })));
const ContractRevisionPage = lazy(() => import('./pages/ContractRevisionPage').then(m => ({ default: m.ContractRevisionPage })));
const PublicCatalogPage = lazy(() => import('./pages/PublicCatalogPage').then(m => ({ default: m.PublicCatalogPage })));
const OrgSettingsPage = lazy(() => import('./pages/OrgSettingsPage').then(m => ({ default: m.OrgSettingsPage })));
const OrgApplicationPage = lazy(() => import('./pages/OrgApplicationPage').then(m => ({ default: m.OrgApplicationPage })));
const AgencyAuthPage = lazy(() => import('./pages/AgencyAuthPage').then(m => ({ default: m.AgencyAuthPage })));
const BriefBuilderPage = lazy(() => import('./pages/BriefBuilderPage'));
const DeliveryHubComposerPage = lazy(() => import('./pages/DeliveryHubComposerPage').then(m => ({ default: m.DeliveryHubComposerPage })));
const DeliveryHubSupervisorPage = lazy(() => import('./pages/DeliveryHubSupervisorPage').then(m => ({ default: m.DeliveryHubSupervisorPage })));
const MyPitchesPage = lazy(() => import('./pages/MyPitchesPage').then(m => ({ default: m.MyPitchesPage })));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage').then(m => ({ default: m.WorkspacePage })));

// ═════════════════════════════════════════════════════════════════════════════
// MAIN APP SHELL
// ═════════════════════════════════════════════════════════════════════════════

// ─── Hash-based page routing ────────────────────────────────────────────────
const VALID_PAGES = new Set([
  'dashboard', 'roster', 'catalog', 'opportunities', 'responses',
  'messages', 'portfolio', 'profile', 'splits', 'admin-dashboard',
  'deal-analyzer', 'contract-revision', 'billing', 'org-settings',
  'apply-agency', 'composer-delivery', 'supervisor-delivery', 'my-pitches',
  'workspace', 'brief-builder',
]);
const LEGAL_PAGES = new Set(['privacy', 'terms', 'dmca']);

function getPageFromHash() {
  const hash = window.location.hash.replace(/^#/, '');
  return VALID_PAGES.has(hash) ? hash : null;
}

function getLegalPageFromHash() {
  const hash = window.location.hash.replace(/^#/, '');
  return LEGAL_PAGES.has(hash) ? hash : null;
}

export default function SongPitch() {
  // ── Public catalog route: render before auth loads ─────────────────────────
  const [catalogSlug] = useState(() => {
    const m = window.location.pathname.match(/^\/catalog\/([^/?#]+)/);
    return m ? m[1] : null;
  });

  // Accept-invite Robotocept: read token from ?invite= before auth loads
  // eslint-disable-next-line no-unused-vars
  const [pendingInviteToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('invite');
    if (t && window.location.hash === '#accept-invite') {
      localStorage.setItem('sp_pending_invite_token', t);
      return t;
    }
    return localStorage.getItem('sp_pending_invite_token') || null;
  });

  // Agency approval token: ?agency_token=TOKEN#agency-signup
  const [agencyToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('agency_token');
    if (t && window.location.hash === '#agency-signup') {
      localStorage.setItem('sp_pending_agency_token', t);
      return t;
    }
    return localStorage.getItem('sp_pending_agency_token') || null;
  });

  const [session, setSession] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [savingRole, setSavingRole] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLanding, setShowLanding] = useState(true);
  // Initialize directly from sessionStorage — supabase.js writes '_cv_email_confirm'
  // before createClient clears the hash, so this is true on the very first render
  // when the user lands from a confirmation link, with no async delay.
  const [showEmailConfirmed, setShowEmailConfirmed] = useState(
    () => sessionStorage.getItem('_cv_email_confirm') === '1'
  );
  const [legalPage, setLegalPage] = useState(() => getLegalPageFromHash()); // 'terms', 'privacy', 'dmca', or null
  const [page, setPage] = useState(() => getPageFromHash() || "dashboard");
  const [deleteAccountModal, setDeleteAccountModal] = useState({ open: false, typed: '', loading: false });
  const [activeMessageConversationId, setActiveMessageConversationId] = useState(null);
  const [stats, setStats] = useState({ songs: 0, users: 0, opportunities: 0, conversations: 0, profileViews: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [badgeCounts, setBadgeCounts] = useState({ messages: 0, responses: 0, opportunities: 0 });
  const [viewingProfile, setViewingProfile] = useState(null);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  // Notification system
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifPanelRef = useRef(null);
  const [supportTargetUserId, setSupportTargetUserId] = useState(null);
  const [supportOpenToken, setSupportOpenToken] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const historyReadyRef = useRef(false);
  const suppressHistoryPushRef = useRef(false);
  const lastHistoryKeyRef = useRef('');
  const pageRef = useRef(page);
  const activeConversationRef = useRef(activeMessageConversationId);
  const skipProfileLoadRef = useRef(false); // prevent race condition after signup
  const stayOnAuthRef = useRef(false); // prevent !session effect from redirecting to landing after a forced signOut
  const oauthInProgressRef = useRef( // true while OAuth callback hash is being processed by Supabase
    window.location.hash.includes('access_token') ||
    new URLSearchParams(window.location.search).has('code')
  );
  // Detect email confirmation.
  // For implicit flow Supabase clears the hash at module-load time (before React
  // renders), so we read the flag from sessionStorage where supabase.js saved it.
  // Fallback to the hash/query for edge-cases (e.g. PKCE flow with ?code=).
  const isEmailConfirmRef = useRef(
    sessionStorage.getItem('_cv_email_confirm') === '1' ||
    window.location.hash.includes('type=signup') ||
    (new URLSearchParams(window.location.search).has('code') && !!localStorage.getItem('sp_pending_profile'))
  );
  // Detect email CHANGE confirmation — same sessionStorage approach for implicit flow.
  const isEmailChangeRef = useRef(
    sessionStorage.getItem('_cv_email_change') === '1' ||
    window.location.hash.includes('type=email_change')
  );
  const legalPageFromHashRef = useRef(!!getLegalPageFromHash()); // true if legal page was loaded from URL
  const suppressLegalPushRef = useRef(false); // prevent double history push on popstate
  const [authError, setAuthError] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  // Global audio player - shared across all pages
  const audioPlayer = useAudioPlayer();

  // Notifications + sidebar badge counts — extracted to custom hook
  const {
    notifications, unreadCount, badgeCounts: notifBadgeCounts,
    loadSidebarBadges, handleMarkRead, handleMarkAllRead, handleDismiss,
  } = useNotifications({ userProfile, pageRef, activeConversationRef });

  // Keep the useState badgeCounts in sync with the hook's computed value
  useEffect(() => {
    setBadgeCounts(notifBadgeCounts);
  }, [notifBadgeCounts]);

  useEffect(() => {
    if (!session || !userProfile) {
      historyReadyRef.current = false;
      lastHistoryKeyRef.current = '';
    }
  }, [session, userProfile]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    activeConversationRef.current = activeMessageConversationId;
  }, [activeMessageConversationId]);

  // ⌘K / Ctrl+K — open command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Sync legal page state ↔ URL hash
  useEffect(() => {
    if (legalPage && LEGAL_PAGES.has(legalPage)) {
      if (legalPageFromHashRef.current) {
        legalPageFromHashRef.current = false;
        window.history.replaceState({ spLegal: true, legalPage }, '', `#${legalPage}`);
      } else if (suppressLegalPushRef.current) {
        suppressLegalPushRef.current = false;
      } else {
        window.history.pushState({ spLegal: true, legalPage }, '', `#${legalPage}`);
      }
    } else if (!legalPage) {
      // Cleared legal page → clean up the URL hash (only if it's still a legal hash)
      if (getLegalPageFromHash()) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [legalPage]);

  // Always show Landing first on a fresh app load. Mark this history entry as landing.
  // IMPORTANT: Do NOT strip the URL if it contains an OAuth callback token —
  // Supabase needs to read #access_token from the hash before we clean the URL.
  useEffect(() => {
    const hasOAuthCallback =
      window.location.hash.includes('access_token') ||
      window.location.hash.includes('error_description') ||
      new URLSearchParams(window.location.search).has('code');
    setShowLanding(true);
    if (!hasOAuthCallback) {
      window.history.replaceState({ spLanding: true }, '', window.location.pathname);
    }
  }, []);

  // Safety reset: when unauthenticated, default back to Landing first.
  // Skip when stayOnAuthRef is set — we intentionally signed out to show an error on AuthPage.
  // Skip when oauthInProgressRef is set — SIGNED_IN will arrive shortly with the real session.
  useEffect(() => {
    if (!session) {
      if (oauthInProgressRef.current) return; // OAuth exchange in flight — wait for SIGNED_IN
      if (stayOnAuthRef.current) {
        stayOnAuthRef.current = false;
        return;
      }
      setShowLanding(true);
    } else {
      oauthInProgressRef.current = false; // Session arrived — OAuth complete
    }
  }, [session]);

  useEffect(() => {
    if (!session || !userProfile) return;

    const key = `${page}|${viewingProfile?.id || ''}`;
    const state = {
      spApp: true,
      page,
      viewingProfile: viewingProfile || null,
    };
    const targetHash = `#${page}`;

    if (!historyReadyRef.current) {
      // If the current history entry is the landing page, push on top so back navigates there.
      // Otherwise replace (e.g. direct URL navigation) to avoid a spurious extra entry.
      if (window.history.state?.spLanding) {
        window.history.pushState(state, '', targetHash);
      } else {
        window.history.replaceState(state, '', targetHash);
      }
      historyReadyRef.current = true;
      lastHistoryKeyRef.current = key;
      return;
    }

    if (suppressHistoryPushRef.current) {
      suppressHistoryPushRef.current = false;
      lastHistoryKeyRef.current = key;
      if (window.location.hash !== targetHash) {
        window.history.replaceState(state, '', targetHash);
      }
      return;
    }

    if (key === lastHistoryKeyRef.current) return;

    window.history.pushState(state, '', targetHash);
    lastHistoryKeyRef.current = key;
  }, [page, viewingProfile, session, userProfile]);

  useEffect(() => {
    const handlePopState = (event) => {
      // Going back to landing page
      if (event.state?.spLanding) {
        setShowLanding(true);
        return;
      }

      // Going back to auth (from app pages)
      if (event.state?.spAuth) {
        setShowLanding(false);
        return;
      }

      // Navigating forward to a legal page
      if (event.state?.spLegal) {
        suppressLegalPushRef.current = true;
        setLegalPage(event.state.legalPage);
        return;
      }

      // If we're currently on a legal page and going back → clear it
      const currentLegal = getLegalPageFromHash();
      if (currentLegal || legalPage) {
        suppressLegalPushRef.current = true;
        setLegalPage(null);
        // Don't return — let the hash fallback below handle the next page
        if (!event.state?.spApp && !getPageFromHash()) return;
      }

      if (event.state?.spApp) {
        suppressHistoryPushRef.current = true;
        setPage(event.state.page || 'dashboard');
        setViewingProfile(event.state.viewingProfile || null);
        setShowNotifPanel(false);
        return;
      }

      // Fallback: read hash
      const hashPage = getPageFromHash();
      if (hashPage) {
        suppressHistoryPushRef.current = true;
        setPage(hashPage);
        setViewingProfile(null);
        setShowNotifPanel(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [legalPage]);

  useEffect(() => {
    function handleHashChange() {
      const legal = getLegalPageFromHash();
      if (legal) setLegalPage(legal);
    }
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isMobileView) {
      
    }
  }, [page, viewingProfile, isMobileView]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      // ── Email change confirmation ─────────────────────────────────────────
      // Fires as USER_UPDATED (same-device) or SIGNED_IN (fresh device/tab).
      // isEmailChangeRef prevents duplicate handling and keeps the spinner
      // alive so neither the landing page nor AccountSetupPage can flash.
      if (event === 'USER_UPDATED' && session) {
        isEmailChangeRef.current = false;
        sessionStorage.removeItem('_cv_email_change');
        setShowLanding(false);
        setPage('profile');
        loadUserProfile(session.user);
        showToast.success('Email updated successfully!');
        return;
      }
      if (session && event === 'SIGNED_IN' && isEmailChangeRef.current) {
        isEmailChangeRef.current = false;
        sessionStorage.removeItem('_cv_email_change');
        setShowLanding(false);
        setPage('profile');
        loadUserProfile(session.user);
        showToast.success('Email updated successfully!');
        return;
      }
      if (session && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        // ── Email confirmation detection ───────────────────────────────────────
        // Two independent signals — either one triggers the "Email Confirmed!" screen:
        //   1. sessionStorage flag (set by supabase.js on fresh page load)
        //   2. sp_pending_profile exists + email was confirmed within the last 15 min
        //      (catches hash-navigation case where the tab was already open and
        //       supabase.js module code didn't re-run)
        const hasPendingProfile = !!localStorage.getItem('sp_pending_profile');
        const confirmedAt = session.user?.email_confirmed_at;
        const isRecentConfirm = !!confirmedAt &&
          (Date.now() - new Date(confirmedAt).getTime()) < 15 * 60 * 1000;
        const isEmailConfirm =
          (isEmailConfirmRef.current && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) ||
          (event === 'SIGNED_IN' && hasPendingProfile && isRecentConfirm);

        if (isEmailConfirm) {
          isEmailConfirmRef.current = false;
          sessionStorage.removeItem('_cv_email_confirm');
          setShowEmailConfirmed(true);
          setShowLanding(false);
          setLoading(false);
          loadUserProfile(session.user); // auto-creates profile from sp_pending_profile in background
          return;
        }
        setShowLanding(false);
        if (skipProfileLoadRef.current) {
          // Signup just set the profile directly — skip redundant query
          skipProfileLoadRef.current = false;
          setLoading(false);
        } else {
          loadUserProfile(session.user);
        }
      } else if (!session) {
        // If an email-change confirmation is pending (USER_UPDATED fires shortly),
        // keep the spinner alive — don't show the landing page in between.
        if (isEmailChangeRef.current) return;
        setUserProfile(null);
        setNeedsOnboarding(false);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Inactivity session timeout (24h) ────────────────────────────────────
  useEffect(() => {
    if (!session) return;
    const INACTIVITY_MS = 24 * 60 * 60 * 1000; // 24 hours
    const WARN_MS = 5 * 60 * 1000; // 5 min to respond before auto sign-out
    let inactivityTimer;
    let warnTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      clearTimeout(warnTimer);
      setShowSessionWarning(false);
      inactivityTimer = setTimeout(() => {
        setShowSessionWarning(true);
        warnTimer = setTimeout(async () => {
          await supabase.auth.signOut();
        }, WARN_MS);
      }, INACTIVITY_MS);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(inactivityTimer);
      clearTimeout(warnTimer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [session]);

  useEffect(() => {
    if (userProfile) {
      loadStats();
      loadSidebarBadges();
      loadAnalytics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile) return;
    loadSidebarBadges();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Auto-show feedback modal: 7 days after account creation, then every 30 days
  useEffect(() => {
    if (!userProfile) return;
    const createdAt = new Date(userProfile.created_at).getTime();
    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    const fourteenDays = 14 * 24 * 60 * 60 * 1000;
    if (now - createdAt < sevenDays) return;
    const lastSubmit = parseInt(localStorage.getItem('cv_feedback_last') || '0', 10);
    if (lastSubmit && now - lastSubmit < thirtyDays) return;
    const lastDismiss = parseInt(localStorage.getItem('cv_feedback_dismissed') || '0', 10);
    if (lastDismiss && now - lastDismiss < fourteenDays) return;
    const timer = setTimeout(() => setFeedbackOpen(true), 3000);
    return () => clearTimeout(timer);
  }, [userProfile]);

  // ─── Web Push: register service worker + subscribe on login ─────────────────
  useEffect(() => {
    if (!userProfile) return;
    if (!isPushSupported()) return;

    (async () => {
      await registerServiceWorker();
      const permission = await requestPermission();
      if (permission === 'granted') {
        await subscribeAndSave(supabase, userProfile.id);
      }
    })();
  }, [userProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotifClick = (notification, targetPage) => {
    if (!notification.is_read) handleMarkRead(notification.id);
    setPage(targetPage);
    setShowNotifPanel(false);
  };

  // Click-outside to close notification panel
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show toast when a new app version is available
  useEffect(() => {
    const onUpdate = () => {
      showToast.info('✨ New version available — reload to update');
    };
    window.addEventListener('sw-update-available', onUpdate);
    return () => window.removeEventListener('sw-update-available', onUpdate);
  }, []);

  // Global spacebar play/pause shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== 'Space') return;

      // Don't Robotocept when user is typing in an input or textarea
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || document.activeElement?.isContentEditable) return;

      // Only act if there's a song loaded
      if (!audioPlayer.playingSong) return;

      e.preventDefault();
      audioPlayer.play(audioPlayer.playingSong);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [audioPlayer]);

  const loadUserProfile = async (authUserOrId) => {
    const authUser = typeof authUserOrId === 'string' ? { id: authUserOrId } : authUserOrId;
    const userId = authUser?.id;
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      // Use maybeSingle() so 0 rows → data:null,error:null instead of throwing PGRST116/406.
      // Filter covers both is_deleted=false (normal active profiles) and is_deleted IS NULL
      // (profiles created before the column existed). UNIQUE constraint on user_id means
      // at most 1 row matches, so maybeSingle() is always safe with the OR filter.
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*, composers(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        // Permission / JWT errors — sign out cleanly
        if (error.code === '42501' || String(error.message).toLowerCase().includes('403') || String(error.message).toLowerCase().includes('jwt')) {
          stayOnAuthRef.current = true;
          setAuthError("Session error. Please sign in again.");
          setUserProfile(null);
          setNeedsOnboarding(false);
          await supabase.auth.signOut();
          return;
        }
        throw error;
      }

      if (!data) {
        // No profile row found — try auto-create from pending signup data, then fallback
        const isOAuthUser = (authUser?.app_metadata?.provider && authUser.app_metadata.provider !== 'email') ||
                              authUser?.identities?.some(i => i.provider !== 'email');
        const pendingProfileRaw = localStorage.getItem('sp_pending_profile');

        if (pendingProfileRaw) {
          try {
            const pending = JSON.parse(pendingProfileRaw);
            const accountType = pending.role === 'executive' ? 'music_executive' : pending.role === 'organization' ? 'organization' : 'composer';
            const trialEndsAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
            const profileRow = {
              user_id: userId,
              role: pending.role,
              account_type: accountType,
              first_name: pending.firstName,
              last_name: pending.lastName,
              bio: pending.bio || null,
              location: pending.location || null,
              is_deleted: false,
              avatar_color: `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`,
              marketing_consent: pending.marketingConsent || false,
              marketing_consent_at: pending.marketingConsentAt || null,
              trial_ends_at: trialEndsAt,
            };

            // Use maybeSingle (not single) so a null result doesn't throw
            const { data: insertedData, error: insertErr } = await supabase
              .from('user_profiles')
              .insert([profileRow])
              .select('*, composers(*)')
              .maybeSingle();

            let profileData = insertedData;

            if (insertErr) {
              // 23505 = unique_violation: profile already exists from a previous attempt
              // Fetch it rather than giving up
              if (insertErr.code === '23505') {
                const { data: existing } = await supabase
                  .from('user_profiles')
                  .select('*, composers(*)')
                  .eq('user_id', userId)
                  .maybeSingle();
                profileData = existing;
              } else {
                throw insertErr;
              }
            }

            if (!profileData) throw new Error('Profile not found after insert');

            // Fire-and-forget: add to Resend marketing audience on genuine new inserts only
            // (insertedData is null on 23505 race-condition fallback, so we skip duplicates)
            if (insertedData) {
              supabase.functions.invoke('add-resend-contact', {
                body: { email: authUser.email, first_name: pending.firstName, last_name: pending.lastName },
              }).catch(() => {});
            }

            if (pending.role === 'composer' && pending.genres?.length > 0) {
              await supabase.from('composers').insert([{
                user_profile_id: profileData.id,
                genres: pending.genres,
              }]);
              const { data: refreshed } = await supabase
                .from('user_profiles')
                .select('*, composers(*)')
                .eq('user_id', userId)
                .maybeSingle();
              localStorage.removeItem('sp_pending_profile');
              localStorage.removeItem('sp_pending_signup_email');
              setUserProfile(refreshed ?? profileData);
              setLoading(false);
              return;
            }

            if (pending.role === 'organization' && pending.orgName) {
              const { data: orgData } = await supabase
                .from('organizations')
                .insert([{
                  name: pending.orgName,
                  website_url: pending.orgWebsite || null,
                  description: pending.orgDescription || null,
                  owner_profile_id: profileData.id,
                }])
                .select('id')
                .maybeSingle();
              if (orgData) {
                await supabase.from('organization_members').insert([{
                  organization_id: orgData.id,
                  profile_id: profileData.id,
                  role: 'admin',
                }]);
                await supabase.from('user_profiles').update({ organization_id: orgData.id }).eq('id', profileData.id);
                profileData.organization_id = orgData.id;
                // Mark the agency application token as used
                const agencyApprovalToken = localStorage.getItem('sp_agency_token');
                if (agencyApprovalToken) {
                  await supabase.from('org_applications')
                    .update({ approval_token_used: true })
                    .eq('approval_token', agencyApprovalToken);
                  localStorage.removeItem('sp_agency_token');
                }
              }
            }

            localStorage.removeItem('sp_pending_profile');
            localStorage.removeItem('sp_pending_signup_email');
            setUserProfile(profileData);
            setLoading(false);
            return;
          } catch (createErr) {
            console.error('Auto-create profile failed:', createErr);
            // Do NOT clear sp_pending_profile here — keep it so the next
            // loadUserProfile call can retry rather than losing the data
            setUserProfile(null);
            setShowLanding(false);
            return;
          }
        }

        if (isOAuthUser) {
          setUserProfile(null);
          setShowLanding(false);
          return;
        }

        const pendingEmail = localStorage.getItem('sp_pending_signup_email');
        const emailConfirmedAt = authUser?.email_confirmed_at;
        const isNewlyConfirmed = emailConfirmedAt && (Date.now() - new Date(emailConfirmedAt).getTime()) < 15 * 60 * 1000;
        if (pendingEmail || isNewlyConfirmed) {
          localStorage.removeItem('sp_pending_signup_email');
          setUserProfile(null);
          setShowLanding(false);
          return;
        }

        // Auth account exists but no profile — sign out with error
        setAuthError("No account found for this email. Please create an account first.");
        stayOnAuthRef.current = true;
        setUserProfile(null);
        await supabase.auth.signOut();
        return;
      }

      {
        const hasValidRole = !!(data?.account_type || data?.role);

        // Elevate to 'pro' during an active trial so users experience the full platform.
        const isOnTrial = data?.trial_ends_at && new Date(data.trial_ends_at) > new Date();
        const profileWithTrial = (isOnTrial && (!data.subscription_tier || data.subscription_tier === 'free'))
          ? { ...data, subscription_tier: 'pro', is_on_trial: true }
          : { ...data, is_on_trial: false };

        setUserProfile(profileWithTrial);

        // If the user belongs to an org, fetch it for tier-resolution in useTier
        if (data?.organization_id) {
          supabase
            .from('organizations')
            .select('id, name, logo_url, subscription_tier, subscription_status')
            .eq('id', data.organization_id)
            .maybeSingle()
            .then(({ data: orgData }) => { if (orgData) setOrganization(orgData); });
        }

        // Consume a pending invite token (stored before auth, or on landing)
        const pendingToken = localStorage.getItem('sp_pending_invite_token');
        if (pendingToken && isExecLike(data?.account_type)) {
          supabase.functions.invoke('accept-org-invite', { body: { token: pendingToken } })
            .then(({ data: inviteResult }) => {
              localStorage.removeItem('sp_pending_invite_token');
              if (inviteResult?.success) {
                showToast.success(`Welcome to ${inviteResult.org?.name}!`);
                setPage('org-settings');
                // Reload profile to pick up organization_id
                loadUserProfile(authUser);
              }
            })
            .catch(() => localStorage.removeItem('sp_pending_invite_token'));
        }

        if (hasValidRole) {
          setNeedsOnboarding(false);
          // Use pageRef (captured at init from the hash) rather than re-reading
          // window.location.hash, which was already stripped by replaceState before
          // this runs — otherwise every refresh resets to dashboard.
          if (!VALID_PAGES.has(pageRef.current)) {
            setPage('dashboard');
          }
        } else {
          setNeedsOnboarding(true);
        }
      }
    } catch (err) {
      console.error("Error loading profile:", err);
    } finally {
      setLoading(false);
    }
  };

  // loadSidebarBadges now comes from useNotifications hook above

  const loadStats = async () => {
    try {
      const isExec = isExecLike(userProfile?.account_type);
      const isComposer = userProfile?.account_type === 'composer';
      const isAdminRole = userProfile?.account_type === 'admin';

      // Base queries (platform totals + conversations)
      const queries = [
        supabase.from('songs').select('id', { count: 'exact', head: true }),
        isAdminRole
          ? supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('is_deleted', false).neq('account_type', 'admin')
          : supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('is_deleted', false).neq('account_type', userProfile?.account_type || ''),
        supabase.from('opportunities').select('id', { count: 'exact', head: true }).eq('status', 'Open'),
        supabase.from('conversations').select('id', { count: 'exact', head: true }).or(`user1_id.eq.${userProfile.id},user2_id.eq.${userProfile.id}`),
      ];

      // Role-specific queries
      if (isComposer) {
        queries.push(
          supabase.from('songs').select('id', { count: 'exact', head: true }).eq('composer_id', userProfile.id),
          supabase.from('profile_views').select('id', { count: 'exact', head: true }).eq('viewed_profile_id', userProfile.id)
        );
      }
      if (isExec || isAdminRole) {
        queries.push(
          supabase.from('opportunities').select('id').eq('creator_id', userProfile.id)
        );
      }

      const results = await Promise.all(queries);
      const [songsRes, usersRes, oppsRes, convsRes] = results;

      const newStats = {
        songs: songsRes.count || 0,
        users: usersRes.count || 0,
        opportunities: oppsRes.count || 0,
        conversations: convsRes.count || 0,
      };

      const roleStartIdx = 4;

      if (isComposer) {
        newStats.mySongs = results[roleStartIdx]?.count || 0;
        newStats.profileViews = results[roleStartIdx + 1]?.count || 0;
      }

      if (isExec || isAdminRole) {
        const execOpps = results[roleStartIdx]?.data || [];
        const oppIds = execOpps.map(o => o.id);
        if (oppIds.length > 0) {
          const { count } = await supabase
            .from('responses')
            .select('id', { count: 'exact', head: true })
            .in('opportunity_id', oppIds);
          newStats.totalResponses = count || 0;
        } else {
          newStats.totalResponses = 0;
        }
      }

      setStats(newStats);
      setBadgeCounts(prev => ({
        messages: prev.messages,
        responses: (isExec || isAdminRole) ? (newStats.totalResponses || 0) : 0,
        opportunities: prev.opportunities,
      }));
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const loadAnalytics = async () => {
    if (!userProfile) return;
    try {
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 86400000);
      const weekAgoISO = weekAgo.toISOString();
      const isAdmin = userProfile.account_type === 'admin';
      const isComposer = userProfile.account_type === 'composer';
      const isExec = isExecLike(userProfile.account_type);
      const result = {};

      // Helper: group rows by day of week (returns array of 7 counts)
      const groupByDay = (rows, dateField = 'created_at') => {
        const days = Array(7).fill(0);
        rows.forEach(r => {
          const d = new Date(r[dateField]);
          const dayIndex = Math.floor((d.getTime() - weekAgo.getTime()) / 86400000);
          if (dayIndex >= 0 && dayIndex < 7) days[dayIndex]++;
        });
        return days;
      };

      if (isComposer || isAdmin) {
        // Profile views this week
        const { data: views } = await supabase
          .from('profile_views')
          .select('created_at')
          .eq('viewed_profile_id', userProfile.id)
          .gte('created_at', weekAgoISO);
        result.profileViewsWeek = groupByDay(views || []);

        // Recent viewers (last 8, for "who viewed my profile")
        const { data: recentViewData } = await supabase
          .from('profile_views')
          .select('viewer_user_id, created_at')
          .eq('viewed_profile_id', userProfile.id)
          .order('created_at', { ascending: false })
          .limit(8);
        if (recentViewData && recentViewData.length > 0) {
          const viewerIds = [...new Set(recentViewData.map(v => v.viewer_user_id).filter(Boolean))];
          const { data: viewerProfiles } = await supabase
            .from('user_profiles')
            .select('id, user_id, first_name, last_name, account_type, avatar_color')
            .in('user_id', viewerIds);
          result.recentViewers = recentViewData
            .filter(v => v.viewer_user_id)
            .map(v => ({ ...v, profile: (viewerProfiles || []).find(p => p.user_id === v.viewer_user_id) }))
            .filter(v => v.profile);
        } else {
          result.recentViewers = [];
        }
      }

      if (isExec || isAdmin) {
        // Responses this week (to their opportunities)
        const { data: myOpps } = await supabase
          .from('opportunities')
          .select('id')
          .eq('creator_id', userProfile.id);
        const oppIds = (myOpps || []).map(o => o.id);
        if (oppIds.length > 0) {
          const { data: resps } = await supabase
            .from('responses')
            .select('created_at')
            .in('opportunity_id', oppIds)
            .gte('created_at', weekAgoISO);
          result.responsesWeek = groupByDay(resps || []);
        } else {
          result.responsesWeek = Array(7).fill(0);
        }
      }

      if (isAdmin) {
        // User signups this week
        const { data: newUsers } = await supabase
          .from('user_profiles')
          .select('created_at')
          .gte('created_at', weekAgoISO)
          .neq('account_type', 'admin');
        result.signupsWeek = groupByDay(newUsers || []);

        // Genre distribution across all songs
        const { data: allSongs } = await supabase
          .from('songs')
          .select('genre');
        const genreCounts = {};
        (allSongs || []).forEach(s => {
          if (s.genre) {
            genreCounts[s.genre] = (genreCounts[s.genre] || 0) + 1;
          }
        });
        result.genreDistribution = Object.entries(genreCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([label, value]) => ({ label, value }));

        // New songs + opportunities per day this week
        const { data: newSongs } = await supabase
          .from('songs')
          .select('created_at')
          .gte('created_at', weekAgoISO);
        result.songsWeek = groupByDay(newSongs || []);

        const { data: newOpps } = await supabase
          .from('opportunities')
          .select('created_at')
          .gte('created_at', weekAgoISO);
        result.oppsWeek = groupByDay(newOpps || []);
      }

      setAnalytics(result);
    } catch (err) {
      console.error('Analytics load failed:', err);
    }
  };

  const handleSignOut = () => {
    unsubscribeAndRemove(supabase).catch(() => {});
    supabase.auth.signOut().catch(() => {});
    setSession(null);
    setUserProfile(null);
    setShowLanding(true);
    setPage("dashboard");
    window.history.replaceState(null, '', window.location.pathname);
  };

  const handleDeleteAccount = () => {
    setDeleteAccountModal({ open: true, typed: '', loading: false });
  };

  const handleConfirmDelete = async () => {
    if (deleteAccountModal.typed !== 'DELETE') return;
    setDeleteAccountModal(s => ({ ...s, loading: true }));
    try {
      const { error } = await supabase.functions.invoke('delete-account', { body: {} });
      if (error) throw error;
      showToast.success('Account deleted permanently.');
      setDeleteAccountModal({ open: false, typed: '', loading: false });
      await supabase.auth.signOut();
      setUserProfile(null);
      setPage('dashboard');
    } catch (err) {
      showToast.error(friendlyError(err));
      setDeleteAccountModal(s => ({ ...s, loading: false }));
    }
  };

  const handleOpenFounderSupport = async () => {
    try {
      const { data: founder, error } = await supabase
        .from('user_profiles')
        .select('id, user_id, first_name, last_name, account_type')
        .eq('account_type', 'admin')
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!founder?.id) {
        showToast.info('Founder support account not found yet.');
        return;
      }

      setSupportTargetUserId(founder.id);
      setSupportOpenToken(Date.now());
      setPage('messages');
      setViewingProfile(null);
    } catch (err) {
      showToast.error(friendlyError(err));
    }
  };

  const handleGetStarted = () => {
    setShowLanding(false);
    window.history.pushState({ spAuth: true }, '', window.location.pathname);
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    } catch (err) {
      showToast.error(friendlyError(err));
    }
  };

  // Legal pages (accessible without auth — checked before loading spinner)
  if (legalPage === 'terms') {
    return <Suspense fallback={null}><TermsOfServicePage onBack={() => setLegalPage(null)} /></Suspense>;
  }
  if (legalPage === 'privacy') {
    return <Suspense fallback={null}><PrivacyPolicyPage onBack={() => setLegalPage(null)} /></Suspense>;
  }
  if (legalPage === 'dmca') {
    return <Suspense fallback={null}><DMCAPage onBack={() => setLegalPage(null)} /></Suspense>;
  }

  // ── Email confirmed success page ─────────────────────────────────────────
  if (showEmailConfirmed) {
    // Sign out then show the sign-in form.
    // If we left the session active and userProfile was still null, the app
    // would fall through to AccountSetupPage. Signing out is cleaner:
    // session=null → AuthPage (sign-in). When they sign in, loadUserProfile
    // auto-creates their profile from sp_pending_profile → dashboard.
    const handleContinue = () => {
      sessionStorage.removeItem('_cv_email_confirm');
      setShowEmailConfirmed(false);
      stayOnAuthRef.current = true; // prevents !session effect from redirecting to landing
      supabase.auth.signOut();      // clears session → AuthPage renders
    };
    return (
      <div className="hero-animated-bg" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        <div style={{ width: '100%', maxWidth: 420, background: DESIGN_SYSTEM.colors.bg.card, borderRadius: 24, padding: 48, border: `1px solid ${DESIGN_SYSTEM.colors.border.light}`, boxShadow: '0 16px 48px rgba(0,0,0,0.4)', textAlign: 'center' }}>
          {/* Gold checkmark */}
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: `${DESIGN_SYSTEM.colors.brand.primary}18`, border: `2px solid ${DESIGN_SYSTEM.colors.brand.primary}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 28px' }}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path d="M8 18L15 25L28 11" stroke="#C9A84C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 style={{ color: DESIGN_SYSTEM.colors.text.primary, fontSize: 28, fontWeight: 800, marginBottom: 10 }}>Email Confirmed!</h1>
          <p style={{ color: DESIGN_SYSTEM.colors.text.secondary, fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
            Thank you for confirming your email.
          </p>
          <p style={{ color: DESIGN_SYSTEM.colors.text.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 36 }}>
            Your account is all set. Sign in below to start using Coda-Vault.
          </p>
          <button
            onClick={handleContinue}
            style={{ width: '100%', background: DESIGN_SYSTEM.colors.brand.primary, color: DESIGN_SYSTEM.colors.text.primary, border: 'none', borderRadius: 10, padding: '14px', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, sans-serif", boxShadow: '0 4px 16px rgba(201,168,76,0.3)', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.background = DESIGN_SYSTEM.colors.brand.light; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = DESIGN_SYSTEM.colors.brand.primary; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Sign In to Coda-Vault →
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <AppShellSkeleton />;
  }

  // Public: agency application form (no auth required)
  if (page === 'apply-agency' && !session) {
    return (
      <Suspense fallback={null}>
        <OrgApplicationPage onBack={() => { setPage('dashboard'); setShowLanding(true); }} />
      </Suspense>
    );
  }

  // Agency approval link: ?agency_token=TOKEN#agency-signup
  if (agencyToken && !session) {
    return (
      <Suspense fallback={null}>
        <AgencyAuthPage
          approvalToken={agencyToken}
          onAuthComplete={(user) => { localStorage.removeItem('sp_pending_agency_token'); setShowLanding(false); loadUserProfile(user); }}
          onGoogleSignIn={handleGoogleSignIn}
          onBack={() => { localStorage.removeItem('sp_pending_agency_token'); setShowLanding(true); }}
        />
      </Suspense>
    );
  }

  // Show landing page first for signed-out users only.
  // Signed-in users should resume their session directly after refresh.
  if (showLanding && !session) {
    return <>
      <LandingPage onGetStarted={handleGetStarted} onLegalPage={setLegalPage} />
      <CookieConsent />
    </>;
  }

  if (!session) {
    return <>
      <AuthPage onAuthComplete={(user, profileData) => { if (profileData) { skipProfileLoadRef.current = true; setShowLanding(false); setUserProfile(profileData); setNeedsOnboarding(false); if (profileData.organization_id) { supabase.from('organizations').select('id, name, logo_url, subscription_tier, subscription_status').eq('id', profileData.organization_id).maybeSingle().then(({ data: orgData }) => { if (orgData) setOrganization(orgData); }); } setPage(isOrg(profileData.account_type) ? 'org-settings' : isExecLike(profileData.account_type) ? 'catalog' : 'portfolio'); try { window.history.replaceState({}, '', '/?welcome=1'); } catch {} } }} onBackToLanding={() => { setShowLanding(true); setAuthError(null); }} onGoogleSignIn={handleGoogleSignIn} onApplyAgency={() => setPage('apply-agency')} initialError={authError} />
      <CookieConsent />
    </>;
  }

  // needsOnboarding is no longer used — profile is created during signup.
  // If somehow triggered, just show auth page.

  if (!userProfile) {
    return <AccountSetupPage user={session.user} onComplete={() => loadUserProfile(session.user)} />;
  }

  const handleNavigate = (pageName, deliveryContext) => {
    if (deliveryContext !== undefined) setActiveDelivery(deliveryContext);
    setPage(pageName);
  };

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: <TrendingUp size={17} />, forAll: true },
    // Composer: Portfolio → Pitches → Splits → (Brief Builder exec-only) → Opportunities → Deal Analyzer → (Responses/Catalog exec-only) → Messages
    { id: "portfolio", label: "My Portfolio", icon: <Music size={17} />, forComposer: true },
    { id: "my-pitches", label: "My Pitches", icon: <Send size={17} />, forComposer: true },
    { id: "splits", label: "Split Sheets", icon: <FileText size={17} />, forComposer: true },
    { id: "brief-builder", label: "Brief Builder", icon: <Sparkles size={17} />, forExecutive: true, forAdmin: true },
    { id: "opportunities", label: "Opportunities", icon: <Briefcase size={17} />, forAll: true, badge: badgeCounts.opportunities },
    { id: "deal-analyzer", label: "Deal Analyzer", icon: <Scale size={17} />, forComposer: true },
    { id: "responses", label: "Responses", icon: <FileText size={17} />, forExecutive: true, badge: badgeCounts.responses },
    { id: "catalog", label: "Search Catalog", icon: <Search size={17} />, forExecutive: true },
    { id: "messages", label: "Messages", icon: <MessageCircle size={17} />, forAll: true, badge: badgeCounts.messages },
    { id: "org-settings", label: "My Organization", icon: <Building2 size={17} />, forExecutive: true, requiresOrg: true },
    { id: "admin-dashboard", label: "Admin Panel", icon: <Shield size={17} />, forAdmin: true },
  ];

  const isAdmin = userProfile.account_type === 'admin';
  const nav = navItems.filter(item => {
    if (item.requiresOrg && !userProfile.organization_id && !isAdmin) return false;
    return (
      item.forAll ||
      (item.forComposer && (userProfile.account_type === 'composer' || isAdmin)) ||
      (item.forExecutive && (isExecLike(userProfile.account_type) || isAdmin)) ||
      (item.forAdmin && isAdmin)
    );
  });

  const renderPage = () => {
    // If viewing another user's profile, show ViewProfilePage
    if (viewingProfile) {
      return (
        <ViewProfilePage
          profileUser={viewingProfile}
          currentUser={userProfile}
          onBack={() => setViewingProfile(null)}
          onOpenMessages={() => { setViewingProfile(null); setPage('messages'); }}
          onNavigate={setPage}
          audioPlayer={audioPlayer}
          isMobile={isMobileView}
        />
      );
    }

    const isComposer = userProfile.account_type === 'composer';
    const isExecutive = isExecLike(userProfile.account_type);
    const fallback = <DashboardPage user={userProfile} stats={stats} onNavigate={setPage} isMobile={isMobileView} analytics={analytics} />;

    switch (page) {
      case "dashboard": return <DashboardPage user={userProfile} stats={stats} onNavigate={setPage} isMobile={isMobileView} analytics={analytics} />;
      case "roster": setPage('catalog'); return null;
      case "catalog": return (isExecutive || isAdmin) ? <CatalogPage audioPlayer={audioPlayer} isMobile={isMobileView} userProfile={userProfile} onViewProfile={setViewingProfile} /> : fallback;
      case "opportunities": return <OpportunitiesPage userProfile={userProfile} onBadgeRefresh={loadSidebarBadges} onViewProfile={setViewingProfile} isMobile={isMobileView} />;
      case "workspace": return (isExecutive || isAdmin || isComposer) ? <WorkspacePage userProfile={userProfile} onNavigate={setPage} isMobile={isMobileView} isComposer={isComposer && !isAdmin} onOpenDelivery={(responseId, ctx) => { setActiveDelivery({ responseId, ...ctx }); setPage('supervisor-delivery'); }} /> : fallback;
      case "brief-builder": return (isExecutive || isAdmin) ? <BriefBuilderPage userProfile={userProfile} isMobile={isMobileView} onNavigate={setPage} /> : fallback;
      case "responses": return (isExecutive || isAdmin) ? <ResponsesPage userProfile={userProfile} onNavigate={setPage} onViewProfile={setViewingProfile} audioPlayer={audioPlayer} isMobile={isMobileView} onOpenDelivery={(responseId, ctx) => { setActiveDelivery({ responseId, ...ctx }); setPage('supervisor-delivery'); }} /> : fallback;
      case "messages": return <MessagesPage userProfile={userProfile} supportTargetUserId={supportTargetUserId} supportOpenToken={supportOpenToken} onBadgeRefresh={loadSidebarBadges} onActiveConversationChange={setActiveMessageConversationId} isMobile={isMobileView} audioPlayer={audioPlayer} onViewProfile={setViewingProfile} />;
      case "portfolio": return (isComposer || isAdmin) ? <PortfolioPage userProfile={userProfile} audioPlayer={audioPlayer} isMobile={isMobileView} onNavigate={setPage} /> : fallback;
      case "profile": return <ProfilePage user={{ ...userProfile, email: session.user.email }} organization={organization} onSignOut={handleSignOut} onProfileUpdate={() => loadUserProfile(session.user)} onDeleteAccount={handleDeleteAccount} onNavigate={setPage} isMobile={isMobileView} />;
      case "splits": return (isComposer || isAdmin) ? <SplitGenerator userProfile={userProfile} isMobile={isMobileView} /> : fallback;
      case "deal-analyzer": return (isComposer || isAdmin) ? <DealAnalyzerPage userProfile={userProfile} isMobile={isMobileView} /> : fallback;
      case "contract-revision": return (isExecutive || isAdmin) ? <ContractRevisionPage userProfile={userProfile} isMobile={isMobileView} /> : fallback;
      case "admin-dashboard": return isAdmin ? <AdminDashboardPage stats={stats} userProfile={userProfile} onNavigate={setPage} onViewProfile={setViewingProfile} isMobile={isMobileView} analytics={analytics} /> : fallback;
      case "billing": return <ProfilePage user={{ ...userProfile, email: session.user.email }} organization={organization} onSignOut={handleSignOut} onProfileUpdate={() => loadUserProfile(session.user)} onDeleteAccount={handleDeleteAccount} onNavigate={setPage} isMobile={isMobileView} initialTab="billing" />;
      case "org-settings": return (isExecutive || isAdmin) ? <OrgSettingsPage userProfile={userProfile} organization={organization} onOrgChange={setOrganization} onProfileRefresh={() => loadUserProfile(session.user)} isMobile={isMobileView} /> : fallback;
      case "my-pitches": return (isComposer || isAdmin) ? <MyPitchesPage userProfile={userProfile} onNavigate={handleNavigate} isMobile={isMobileView} /> : fallback;
      case "composer-delivery": return (isComposer || isAdmin) ? <DeliveryHubComposerPage userProfile={userProfile} activeDelivery={activeDelivery} onBack={() => setPage('my-pitches')} onNavigate={setPage} isMobile={isMobileView} /> : fallback;
      case "supervisor-delivery": return (isExecutive || isAdmin) ? <DeliveryHubSupervisorPage userProfile={userProfile} activeDelivery={activeDelivery} onBack={() => setPage('responses')} onNavigate={setPage} isMobile={isMobileView} /> : fallback;
      default: return fallback;
    }
  };

  const isSidebarCollapsed = !isMobileView && sidebarCollapsed;
  const desktopSidebarWidth = isSidebarCollapsed ? 96 : 240;

  // Public catalog route — bypass the full app shell entirely
  if (catalogSlug) {
    return (
      <Suspense fallback={<CatalogPageSkeleton />}>
        <PublicCatalogPage slug={catalogSlug} />
      </Suspense>
    );
  }

  return (
    <div className="app-root w-full min-h-screen flex flex-col md:flex-row" style={{ display: "flex", width: '100%', minHeight: '100vh', height: "100vh", background: DESIGN_SYSTEM.colors.bg.secondary, fontFamily: DESIGN_SYSTEM.font.body, color: DESIGN_SYSTEM.colors.text.primary, overflow: "hidden", position: 'relative' }}>
      {/* Skip to content link for keyboard/screen reader users */}
      <a href="#main-content" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden', zIndex: 9999 }} onFocus={e => { e.currentTarget.style.left = '16px'; e.currentTarget.style.top = '16px'; e.currentTarget.style.width = 'auto'; e.currentTarget.style.height = 'auto'; e.currentTarget.style.overflow = 'visible'; e.currentTarget.style.background = DESIGN_SYSTEM.colors.brand.primary; e.currentTarget.style.color = '#fff'; e.currentTarget.style.padding = '8px 16px'; e.currentTarget.style.borderRadius = '8px'; e.currentTarget.style.fontSize = '14px'; e.currentTarget.style.fontWeight = '600'; e.currentTarget.style.textDecoration = 'none'; }} onBlur={e => { e.currentTarget.style.left = '-9999px'; e.currentTarget.style.width = '1px'; e.currentTarget.style.height = '1px'; e.currentTarget.style.overflow = 'hidden'; }}>Skip to content</a>

      {!isMobileView && <nav aria-label="Main navigation" className={isSidebarCollapsed ? 'sidebar-collapsed' : ''} style={{
        width: desktopSidebarWidth,
        minWidth: desktopSidebarWidth,
        background: DESIGN_SYSTEM.colors.nav.bg,
        borderRight: `1px solid ${DESIGN_SYSTEM.colors.nav.border}`,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        position: 'relative',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 1200,
        overflow: 'hidden',
        transform: 'none',
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        <div style={{ padding: "22px 20px", borderBottom: `1px solid ${DESIGN_SYSTEM.colors.nav.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Logo - Professional presentation */}
            <img
              src="/cv-logo.svg"
              alt="Coda-Vault"
              style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 8, margin: isSidebarCollapsed ? '0 auto' : undefined }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {!isSidebarCollapsed && (
              <div>
                <div style={{
                  fontSize: 19,
                  fontWeight: 700,
                  color: DESIGN_SYSTEM.colors.nav.text,
                  letterSpacing: "-0.3px",
                  fontFamily: DESIGN_SYSTEM.font.body,
                }}>Coda-Vault</div>
              </div>
            )}
            <div style={{ marginLeft: isSidebarCollapsed ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
              {!isSidebarCollapsed && !isMobileView && (
                <button aria-label="Collapse sidebar" onClick={() => setSidebarCollapsed(true)} style={{ background: 'transparent', border: 'none', color: DESIGN_SYSTEM.colors.nav.textTertiary, cursor: 'pointer', padding: 6 }}>
                  <ChevronLeft size={18} />
                </button>
              )}
            </div>
          </div>
          {/* header now-playing label removed for aesthetic */}
        </div>

        <nav style={{ flex: 1, padding: "12px 10px", overflowY: "auto", display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nav.map(item => (
            <button key={item.id} aria-label={item.label} className="sidebar-nav-item" onClick={() => { setPage(item.id); setViewingProfile(null); }} style={{
              width: "100%", display: "flex", alignItems: "center", gap: isSidebarCollapsed ? 0 : 10, padding: isSidebarCollapsed ? "10px 6px" : "10px 12px", borderRadius: 8, border: "none", cursor: "pointer", textAlign: "left", fontFamily: DESIGN_SYSTEM.font.body, fontSize: 14, fontWeight: page === item.id ? 600 : 400, transition: "all 0.15s", position: 'relative',
              color: page === item.id ? DESIGN_SYSTEM.colors.nav.activeText : DESIGN_SYSTEM.colors.nav.textMuted,
              background: page === 'none',
              justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
              borderLeft: 'none',
            }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, color: page === item.id ? DESIGN_SYSTEM.colors.nav.activeText : 'inherit' }}>{item.icon}</span>
              {!isSidebarCollapsed && <span>{item.label}</span>}
              {item.badge > 0 && (
                <span style={{
                  position: isSidebarCollapsed ? 'absolute' : 'static',
                  top: isSidebarCollapsed ? 2 : undefined,
                  right: isSidebarCollapsed ? 6 : undefined,
                  marginLeft: isSidebarCollapsed ? 0 : 'auto',
                  background: DESIGN_SYSTEM.colors.brand.primary,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 5px',
                  lineHeight: 1,
                }}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          ))}
          {userProfile.account_type !== 'admin' && (
            <button
              aria-label="Chat with Founder"
              onClick={handleOpenFounderSupport}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: isSidebarCollapsed ? 0 : 10,
                padding: isSidebarCollapsed ? "10px 6px" : "10px 12px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: DESIGN_SYSTEM.font.body,
                fontSize: 14,
                fontWeight: 600,
                transition: "all 0.15s",
                position: 'relative',
                marginTop: 6,
                background: 'none',
                color: DESIGN_SYSTEM.colors.brand.primary,
                justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                borderLeft: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'none'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              title="Chat with Support"
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20 }}>
                <MessageCircle size={17} />
              </span>
              {!isSidebarCollapsed && <span> Chat with Support</span>}
            </button>
          )}

          {/* Share Feedback */}
          <button
            onClick={() => setFeedbackOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: isSidebarCollapsed ? 0 : 10,
              padding: isSidebarCollapsed ? "10px 6px" : "10px 12px",
              borderRadius: 8, border: 'none', cursor: 'pointer', width: '100%',
              background: 'transparent', color: DESIGN_SYSTEM.colors.nav.textTertiary,
              justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
              fontSize: 13, fontWeight: 500, transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = DESIGN_SYSTEM.colors.nav.hoverBg; e.currentTarget.style.color = DESIGN_SYSTEM.colors.nav.textMuted; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = DESIGN_SYSTEM.colors.nav.textTertiary; }}
            title="Share Feedback"
          >
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20 }}>
              <svg width={17} height={17} viewBox="0 0 24 24" fill="#f0bf6b" stroke="#f0bf6b" strokeWidth="1.5">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
            </span>
            {!isSidebarCollapsed && <span>Share Feedback</span>}
          </button>
        </nav>

        {/* ⌘K search hint */}
        {!isSidebarCollapsed && (
          <div style={{ padding: "0 10px 4px" }}>
            <button
              onClick={() => setCmdOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 16px',
                background: DESIGN_SYSTEM.colors.nav.searchBg,
                border: `1px solid ${DESIGN_SYSTEM.colors.nav.border}`,
                borderRadius: 10,
                color: DESIGN_SYSTEM.colors.nav.textTertiary,
                fontFamily: DESIGN_SYSTEM.font.body,
                fontSize: 13, cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: 8,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = DESIGN_SYSTEM.colors.nav.hoverBg; e.currentTarget.style.color = DESIGN_SYSTEM.colors.nav.textMuted; }}
              onMouseLeave={e => { e.currentTarget.style.background = DESIGN_SYSTEM.colors.nav.searchBg; e.currentTarget.style.color = DESIGN_SYSTEM.colors.nav.textTertiary; }}
            >
              <span style={{ flex: 1, textAlign: 'left' }}>Search...</span>
              <kbd style={{ background: DESIGN_SYSTEM.colors.nav.kbdBg, border: `1px solid ${DESIGN_SYSTEM.colors.nav.border}`, borderRadius: 5, padding: '2px 6px', fontSize: 12 }}>⌘K</kbd>
            </button>
          </div>
        )}

        <div style={{ padding: "0 10px 8px" }}>
          <div ref={notifPanelRef} style={{ position: 'relative' }}>
            <button
              aria-label="Notifications"
              onClick={() => {
                if (!showNotifPanel && notifications.some(n => !n.is_read)) {
                  handleMarkAllRead();
                }
                setShowNotifPanel(p => !p);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: isSidebarCollapsed ? 0 : 10,
                padding: isSidebarCollapsed ? "10px 6px" : "10px 12px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: DESIGN_SYSTEM.font.body,
                fontSize: 14,
                fontWeight: unreadCount > 0 ? 600 : 400,
                transition: "all 0.15s",
                position: 'relative',
                background: showNotifPanel ? DESIGN_SYSTEM.colors.nav.activeBg : "transparent",
                color: showNotifPanel ? DESIGN_SYSTEM.colors.nav.activeText : DESIGN_SYSTEM.colors.nav.textMuted,
                justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                borderLeft: showNotifPanel ? `3px solid ${DESIGN_SYSTEM.colors.nav.activeText}` : '3px solid transparent',
              }}
              onMouseEnter={e => { if (!showNotifPanel) { e.currentTarget.style.background = DESIGN_SYSTEM.colors.nav.hoverBg; e.currentTarget.style.color = DESIGN_SYSTEM.colors.nav.text; } }}
              onMouseLeave={e => { if (!showNotifPanel) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = DESIGN_SYSTEM.colors.nav.textMuted; } }}
              title="Notifications"
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, color: unreadCount > 0 ? DESIGN_SYSTEM.colors.brand.primary : 'inherit' }}>
                <Bell size={18} />
              </span>
              {!isSidebarCollapsed && <span>Notifications</span>}
              {unreadCount > 0 && (
                <span style={{
                  position: isSidebarCollapsed ? 'absolute' : 'static',
                  top: isSidebarCollapsed ? 2 : undefined,
                  right: isSidebarCollapsed ? 6 : undefined,
                  marginLeft: isSidebarCollapsed ? 0 : 'auto',
                  background: DESIGN_SYSTEM.colors.brand.primary,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 5px',
                  lineHeight: 1,
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifPanel && (
              <NotificationPanel
                notifications={notifications}
                loading={false}
                onMarkAllRead={handleMarkAllRead}
                onNotifClick={handleNotifClick}
                onDismiss={handleDismiss}
                onClose={() => setShowNotifPanel(false)}
                sidebarLeft={desktopSidebarWidth + 8}
              />
            )}
          </div>
        </div>

        <div style={{ padding: "14px 14px", borderTop: `1px solid ${DESIGN_SYSTEM.colors.nav.border}` }}>
          <div
            onClick={() => { setPage('profile'); setViewingProfile(null); }}
            style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', cursor: 'pointer', padding: '6px 4px', borderRadius: 8, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = DESIGN_SYSTEM.colors.nav.hoverBg}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Avatar name={`${userProfile.first_name} ${userProfile.last_name}`} color={userProfile.avatar_color} avatarUrl={userProfile.avatar_url} size={34} border="1px solid rgba(201,168,76,0.3)" />
            {!isSidebarCollapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: DESIGN_SYSTEM.colors.nav.text, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userProfile.first_name} {userProfile.last_name}</div>
                <div style={{ color: DESIGN_SYSTEM.colors.nav.textTertiary, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {userProfile.account_type === 'admin' ? (
                    <Badge color={DESIGN_SYSTEM.colors.brand.primary}>👋 Founder</Badge>
                  ) : (
                    <>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: DESIGN_SYSTEM.colors.brand.primary, display: 'inline-block' }}></span>
                      {accountLabel(userProfile.account_type)}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {!isSidebarCollapsed && (() => {
            const fields = (userProfile.account_type === 'composer' || userProfile.account_type === 'admin')
              ? [userProfile.bio, userProfile.location, userProfile.avatar_url, userProfile.pro_name || userProfile.pro, userProfile.role, Array.isArray(userProfile.genres) && userProfile.genres.length > 0, userProfile.instruments]
              : isOrg(userProfile.account_type)
                ? [userProfile.bio, userProfile.location, userProfile.avatar_url, Array.isArray(userProfile.genres) && userProfile.genres.length > 0]
                : [userProfile.bio, userProfile.location, userProfile.avatar_url, userProfile.company, userProfile.job_title, Array.isArray(userProfile.genres) && userProfile.genres.length > 0];
            const filled = fields.filter(Boolean).length;
            const total = fields.length;
            if (filled >= total) return null;
            const pct = Math.round((filled / total) * 100);
            return (
              <div
                onClick={() => { setPage('profile'); setViewingProfile(null); }}
                style={{ marginTop: 8, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = DESIGN_SYSTEM.colors.nav.hoverBg}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: DESIGN_SYSTEM.colors.brand.primary, fontWeight: 600 }}>Complete your profile</span>
                  <span style={{ fontSize: 12, color: DESIGN_SYSTEM.colors.nav.textTertiary }}>{pct}%</span>
                </div>
                <div style={{ height: 3, background: DESIGN_SYSTEM.colors.bg.elevated, borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: DESIGN_SYSTEM.colors.brand.primary, borderRadius: 2, transition: 'width 0.3s ease' }} />
                </div>
              </div>
            );
          })()}
        </div>
      </nav>}

      {!isMobileView && isSidebarCollapsed && (
        <button
          aria-label="Expand sidebar"
          onClick={() => setSidebarCollapsed(false)}
          style={{
            position: 'fixed',
            top: 24,
            left: 104,
            zIndex: 1000,
            background: DESIGN_SYSTEM.colors.bg.card,
            border: `1px solid ${DESIGN_SYSTEM.colors.border.light}`,
            color: DESIGN_SYSTEM.colors.text.tertiary,
            cursor: 'pointer',
            padding: 6,
            borderRadius: 8,
            boxShadow: DESIGN_SYSTEM.shadow.sm,
          }}
          title="Expand sidebar"
        >
          <ChevronRight size={18} />
        </button>
      )}

      <main id="main-content" className="page-fade-in flex-1 w-full min-w-0" key={viewingProfile ? `profile-${viewingProfile.id}` : page} style={{ flex: 1, width: '100%', minWidth: 0, overflowY: "auto", background: DESIGN_SYSTEM.colors.bg.primary, paddingBottom: isMobileView ? `calc(68px + env(safe-area-inset-bottom, 0px) + ${audioPlayer.playingSong ? 70 : 0}px)` : (audioPlayer.playingSong ? 70 : 0), position: 'relative', paddingTop: 0 }}>
        <Suspense fallback={<PageSkeleton />}>
          {renderPage()}
        </Suspense>
      </main>

      {/* Bottom Tab Navigation — mobile only */}
      {isMobileView && (
        <BottomTabNav
          page={viewingProfile ? 'profile' : page}
          accountType={userProfile.account_type}
          badgeCounts={badgeCounts}
          onNavigate={(id) => { setPage(id); setViewingProfile(null); }}
          audioPlayerActive={!!audioPlayer.playingSong}
          onSupportOpen={handleOpenFounderSupport}
        />
      )}

      {/* Now Playing Bar */}
      <NowPlayingBar
        playingSong={audioPlayer.playingSong}
        isPlaying={audioPlayer.isPlaying}
        currentTime={audioPlayer.currentTime}
        duration={audioPlayer.duration}
        onPlay={audioPlayer.play}
        onStop={audioPlayer.stop}
        onRestart={audioPlayer.restart}
        onSkipBack={audioPlayer.skipBack}
        onSkipForward={audioPlayer.skipForward}
        onSeekTo={audioPlayer.seekTo}
        volume={audioPlayer.volume}
        isMuted={audioPlayer.isMuted}
        onVolumeChange={audioPlayer.setVolumeLevel}
        onToggleMute={audioPlayer.toggleMute}
        waveformPeaks={audioPlayer.waveformPeaks}
        sidebarCollapsed={isSidebarCollapsed}
        sidebarOffset={isMobileView ? 0 : desktopSidebarWidth}
      />

      {/* Cookie consent banner */}
      <CookieConsent />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        userProfile={userProfile}
      />

      {/* Session inactivity warning */}
      {showSessionWarning && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(4,5,14,0.78)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div className="modal-enter" style={{ background: DESIGN_SYSTEM.colors.bg.card, borderRadius: 20, padding: 32, maxWidth: 380, width: '100%', border: `1px solid ${DESIGN_SYSTEM.colors.border.light}`, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
            <h2 style={{ color: DESIGN_SYSTEM.colors.text.primary, fontSize: 20, fontWeight: 800, fontFamily: DESIGN_SYSTEM.font.body, marginBottom: 10 }}>Still there?</h2>
            <p style={{ color: DESIGN_SYSTEM.colors.text.tertiary, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
              You've been inactive for a while. For your security, we'll sign you out in 5 minutes unless you're still here.
            </p>
            <button
              onClick={() => setShowSessionWarning(false)}
              style={{ background: DESIGN_SYSTEM.colors.brand.primary, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: DESIGN_SYSTEM.font.body, boxShadow: '0 4px 16px rgba(201,168,76,0.25)' }}
              onMouseEnter={e => e.currentTarget.style.background = DESIGN_SYSTEM.colors.brand.light}
              onMouseLeave={e => e.currentTarget.style.background = DESIGN_SYSTEM.colors.brand.primary}
            >
              Yes, I'm here
            </button>
          </div>
        </div>
      )}

      {/* ── Delete Account Modal ─────────────────────────────────────────── */}
      {deleteAccountModal.open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(4,5,14,0.82)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div style={{
            background: DESIGN_SYSTEM.colors.bg.card,
            border: '1px solid rgba(248,113,113,0.25)',
            borderRadius: 20, padding: 32,
            maxWidth: 420, width: '100%',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          }}>
            {/* Icon */}
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 20,
            }}>
              <AlertTriangle size={22} color="#f87171" />
            </div>

            <h3 style={{ color: DESIGN_SYSTEM.colors.text.primary, fontSize: 18, fontWeight: 700, marginBottom: 8, fontFamily: DESIGN_SYSTEM.font.body }}>
              Delete your account?
            </h3>
            <p style={{ color: DESIGN_SYSTEM.colors.text.secondary, fontSize: 14, lineHeight: 1.6, marginBottom: 20, fontFamily: DESIGN_SYSTEM.font.body }}>
              This will permanently delete your profile, songs, messages, and all associated data. <strong style={{ color: DESIGN_SYSTEM.colors.text.primary }}>This cannot be undone.</strong>
            </p>

            {/* Type to confirm */}
            <p style={{ color: DESIGN_SYSTEM.colors.text.muted, fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8, fontFamily: DESIGN_SYSTEM.font.body }}>
              Type <span style={{ color: '#f87171', fontFamily: 'monospace' }}>DELETE</span> to confirm
            </p>
            <input
              type="text"
              value={deleteAccountModal.typed}
              onChange={e => setDeleteAccountModal(s => ({ ...s, typed: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleConfirmDelete()}
              placeholder="DELETE"
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                background: DESIGN_SYSTEM.colors.bg.primary,
                border: `1px solid ${deleteAccountModal.typed === 'DELETE' ? '#f87171' : DESIGN_SYSTEM.colors.border.light}`,
                borderRadius: 10, padding: '11px 14px',
                color: '#f87171', fontSize: 14, fontFamily: 'monospace',
                outline: 'none', marginBottom: 20,
                letterSpacing: '1px',
              }}
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setDeleteAccountModal({ open: false, typed: '', loading: false })}
                disabled={deleteAccountModal.loading}
                style={{
                  flex: 1, padding: '11px', borderRadius: 10, cursor: 'pointer',
                  background: 'transparent', border: `1px solid ${DESIGN_SYSTEM.colors.border.light}`,
                  color: DESIGN_SYSTEM.colors.text.secondary, fontSize: 14, fontWeight: 600,
                  fontFamily: DESIGN_SYSTEM.font.body,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteAccountModal.typed !== 'DELETE' || deleteAccountModal.loading}
                style={{
                  flex: 1, padding: '11px', borderRadius: 10,
                  cursor: deleteAccountModal.typed !== 'DELETE' || deleteAccountModal.loading ? 'not-allowed' : 'pointer',
                  background: deleteAccountModal.typed === 'DELETE' ? '#ef4444' : 'rgba(248,113,113,0.15)',
                  border: 'none',
                  color: deleteAccountModal.typed === 'DELETE' ? '#fff' : '#f87171',
                  fontSize: 14, fontWeight: 700,
                  fontFamily: DESIGN_SYSTEM.font.body,
                  opacity: deleteAccountModal.loading ? 0.6 : 1,
                  transition: 'all 0.15s',
                }}
              >
                {deleteAccountModal.loading ? 'Deleting…' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <Toaster position="bottom-right" toastOptions={{ duration: 3500 }} />

      {/* Command Palette */}
      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        onNavigate={(pg) => { setPage(pg); setViewingProfile(null); setCmdOpen(false); }}
      />
    </div>
  );
}
